import { useCallback, useEffect, useState } from 'react';
import { apiFetch, type ApiError } from '../lib/api.js';
import { useAuth } from '../context/AuthContext.js';

interface DataInfo {
  messageCount: number;
  eventCount: number;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE');
  } catch {
    return iso;
  }
}

export default function PrivacyPage({ onClose }: { onClose: () => void }) {
  const { user, logout } = useAuth();
  const [info, setInfo] = useState<DataInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState<'export' | 'delete' | null>(null);

  const loadInfo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<DataInfo>('/api/gdpr/me/info');
      setInfo(data);
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr?.status === 429 ? 'Zu viele Anfragen — bitte kurz warten.' : 'Konnte Daten nicht laden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadInfo();
  }, [loadInfo]);

  const onExport = useCallback(async () => {
    setBusy('export');
    setError(null);
    try {
      const res = await fetch('/api/gdpr/me/export', { credentials: 'include' });
      if (!res.ok) {
        if (res.status === 429) setError('Zu viele Anfragen — bitte kurz warten.');
        else setError('Export fehlgeschlagen.');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('Content-Disposition') || '';
      const match = /filename="([^"]+)"/.exec(cd);
      a.download = match?.[1] ?? `chat-tool-export-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('export failed', err);
      setError('Export fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  }, []);

  const onConfirmDelete = useCallback(async () => {
    setBusy('delete');
    setError(null);
    try {
      await apiFetch('/api/gdpr/me', { method: 'DELETE' });
      await logout();
      onClose();
    } catch (err) {
      const apiErr = err as ApiError;
      setError(
        apiErr?.status === 429
          ? 'Zu viele Anfragen — bitte kurz warten.'
          : 'Löschung fehlgeschlagen. Bitte später erneut versuchen.',
      );
    } finally {
      setBusy(null);
      setConfirmOpen(false);
    }
  }, [logout, onClose]);

  if (!user) {
    return (
      <div className="p-6 text-slate-300">
        <p>Bitte logge dich ein, um deine Datenschutz-Einstellungen zu sehen.</p>
        <button onClick={onClose} className="mt-3 text-sm text-slate-400 underline">
          Zurück
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="flex items-center gap-3 px-4 py-3 border-b border-slate-800">
        <button onClick={onClose} className="text-sm text-slate-400 hover:text-slate-200">
          ← Zurück
        </button>
        <h1 className="text-lg font-semibold">Datenschutz</h1>
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-6">
        <section className="space-y-2">
          <h2 className="text-base font-semibold">Wie wir deine Chat-Daten verarbeiten</h2>
          <p className="text-sm text-slate-300">
            Wir speichern deine Chat-Nachrichten und ‑Events <strong>90 Tage</strong> zur
            Moderation und Verlaufs-Anzeige im Stream. Danach werden sie automatisch gelöscht.
          </p>
          <p className="text-sm text-slate-400">
            Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse Moderation).
            Du kannst deine Daten jederzeit exportieren oder löschen lassen.
          </p>
        </section>

        <section className="space-y-2 border border-slate-800 rounded p-3 bg-slate-900/40">
          <h3 className="text-sm font-semibold">Deine gespeicherten Daten</h3>
          {loading ? (
            <p className="text-sm text-slate-400">Lade…</p>
          ) : error ? (
            <p className="text-sm text-red-400">{error}</p>
          ) : info ? (
            <dl className="text-sm grid grid-cols-2 gap-y-1 gap-x-4">
              <dt className="text-slate-400">Nachrichten</dt>
              <dd>{info.messageCount.toLocaleString('de-DE')}</dd>
              <dt className="text-slate-400">Events</dt>
              <dd>{info.eventCount.toLocaleString('de-DE')}</dd>
              <dt className="text-slate-400">Zuerst gesehen</dt>
              <dd>{formatDate(info.firstSeenAt)}</dd>
              <dt className="text-slate-400">Zuletzt gesehen</dt>
              <dd>{formatDate(info.lastSeenAt)}</dd>
            </dl>
          ) : null}
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Deine Rechte</h3>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => void onExport()}
              disabled={busy !== null}
              className="px-3 py-2 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-sm"
            >
              {busy === 'export' ? 'Exportiere…' : 'Meine Daten exportieren (JSON)'}
            </button>
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={busy !== null}
              className="px-3 py-2 rounded bg-red-700 hover:bg-red-600 disabled:opacity-50 text-sm"
            >
              Meine Daten löschen
            </button>
          </div>
          <p className="text-xs text-slate-500">
            Bei einer Löschung werden deine Nachrichten anonymisiert (der Benutzername wird
            entfernt), damit Moderationsverläufe nachvollziehbar bleiben. Dein Account wird
            entfernt — du wirst danach automatisch ausgeloggt.
          </p>
          {error && busy === null && <p className="text-sm text-red-400">{error}</p>}
        </section>
      </main>

      {confirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
        >
          <div className="bg-slate-900 border border-slate-700 rounded max-w-md w-full p-4 space-y-3">
            <h2 className="text-base font-semibold">Wirklich löschen?</h2>
            <p className="text-sm text-slate-300">
              Deine Chat-Nachrichten werden anonymisiert, dein Account wird gelöscht, du wirst
              ausgeloggt. Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={busy === 'delete'}
                className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-sm"
              >
                Abbrechen
              </button>
              <button
                onClick={() => void onConfirmDelete()}
                disabled={busy === 'delete'}
                className="px-3 py-1.5 rounded bg-red-700 hover:bg-red-600 disabled:opacity-50 text-sm"
              >
                {busy === 'delete' ? 'Lösche…' : 'Endgültig löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
